import { Table } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { sumBy } from "lodash";
import React from "react";
import { useHistory } from "react-router-dom";
import { Permission, Purchase, Tag, XolaBotAssistType, XolaBotAssistTypeCopy } from "types";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { getAddonLineItems } from "../../../models/PurchaseItem";
import { DateParamsProps } from "../../../stores/reports/analyticsFilterStore";
import {
    useXolaBotEarningsReportFilterStore,
    XolaBotEarningsReportFilter,
} from "../../../stores/reports/xolabotEarningsReportFilterStore";
import { Currency } from "../../Common/Currency";
import { ColumnLayout } from "../../Layout/ColumnLayout";
import { PurchaseProductImage } from "../../PurchasesMenu/PurchaseProductImage";

export const EarningsReportTransactionsList = () => {
    const history = useHistory();
    const { isGranted } = useAuthorization();

    const filters = useXolaBotEarningsReportFilterStore((state) => state.filters);
    const dateParams = useXolaBotEarningsReportFilterStore((state) => state.getDateParams());
    const params = getXolaBotEarningReportParams(dateParams, filters);

    const { data: purchases } = useAutoFetchCollection<Purchase>("purchases", params);

    const showPurchases = isGranted(Permission.PURCHASES_VIEW);
    const handleRowClick = (id?: ID) => {
        if (!id || !showPurchases) {
            return null;
        }

        history.push(`/purchases/${id}`);
    };

    if (!purchases) {
        return null;
    }

    return (
        <ColumnLayout.Fill>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Item</Table.Header>
                        <Table.Header>Date</Table.Header>
                        <Table.Header>Assist</Table.Header>
                        <Table.Header>Generated Revenue</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {purchases.map((purchase) => {
                        const xolaBotTag = purchase.tags?.find((tag) => tag.id in XolaBotAssistTypeCopy);
                        const feeAmount = getGeneratedAmount(purchase, xolaBotTag);

                        if (!xolaBotTag) {
                            return null;
                        }

                        return (
                            <Table.Row
                                key={purchase.id}
                                className={clsx("w-24 pr-0", showPurchases && "cursor-pointer")}
                                onClick={() => handleRowClick(purchase.id)}
                            >
                                <Table.Cell>
                                    <PurchaseProductImage purchase={purchase} />
                                </Table.Cell>
                                <Table.Cell className="no-translate">
                                    {formatDate(purchase.createdAt, DateFormat.DATE_SHORT)}
                                </Table.Cell>
                                <Table.Cell>
                                    {xolaBotTag?.id && XolaBotAssistTypeCopy[xolaBotTag.id as XolaBotAssistType]}
                                </Table.Cell>
                                <Table.Cell>
                                    <FeeWithCurrency amount={feeAmount} />
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </ColumnLayout.Fill>
    );
};

const FeeWithCurrency = ({ amount }: { amount: number }) => {
    return (
        <div className={clsx(amount < 0 ? "text-red" : "text-black")}>
            <Currency>{amount}</Currency>
        </div>
    );
};

const getGeneratedAmount = (purchase: Purchase, xolaBotTag?: Tag) => {
    let total = purchase.amount;

    if (xolaBotTag?.id === XolaBotAssistType.UPSELL_ADDONS) {
        const purchaseAddons = purchase.items.flatMap((item) => getAddonLineItems(item));
        total = sumBy(purchaseAddons, "amount");
    }

    return total;
};

export const getXolaBotEarningReportParams = (
    dateParams: DateParamsProps,
    filters: XolaBotEarningsReportFilter,
    format?: "xlsx",
) => {
    const baseParams: any = { object: "purchase", ...dateParams, format };

    const filterParams =
        filters.assistTypes.selected.length > 0
            ? { "tags._id[in]": filters.assistTypes.selected.join(",") }
            : { "tags._id[in]": "XolaBot" };

    return { ...baseParams, ...filterParams };
};
